import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import AppContext from "../../../../context/AppContext";
import { getEmailData, getJobAddress } from "../../../../services/EmailService";
import { getJobOrders } from "../../../../services/OrderService";
import { convertDate } from "../../../../utils/function";

import UnitMaterialsTable from "../../../../components/CustomTable/UnitMaterialsTable";
import TotalsSummary from "../TotalsSummary";

const useStyles = makeStyles({
  root: {},
  navLink: {
    textDecoration: "none",
  },
});

const defaultTaxRate = 0.085;
const websiteURL = "https://horizoncarpentry.com";

export default function OrderPreview() {
  const classes = useStyles();
  const { user } = useContext(AppContext);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderId = params.get("oid"); // CHANGE_REQUEST_ID
  const jobId = params.get("jid"); //JOB_ID
  const supplierId = params.get("sid") || 0; //SUPPLIER_ID
  const unitId = params.get("unit"); //UNIT_ID
  const [emailData, setEmailData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [unitMaterials, setUnitMaterials] = useState([]);
  const [taxRate, setTaxRate] = useState(defaultTaxRate);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingEmailData, setLoadingEmailData] = useState(false);

  const retrieveEmailData = useCallback(async (JID, SID) => {
    try {
      setLoadingEmailData(true);
      getEmailData(JID, SID).then((res) => {
        const newEmailData = res[0];

        setLoadingEmailData(false);
        setEmailData(newEmailData);
        if (newEmailData?.TAX_RATE !== null) setTaxRate(newEmailData?.TAX_RATE);
      });
    } catch (e) {
      console.error(e);
      setLoadingEmailData(false);
    }
  }, []);

  const retrieveJobAddress = useCallback(async (JID, UNIT) => {
    try {
      setLoadingEmailData(true);
      getJobAddress(JID, UNIT).then((res) => {
        const newJobAddressData = res[0];

        setLoadingEmailData(false);
        setEmailData(newJobAddressData);
      });
    } catch (e) {
      console.error(e);
      setLoadingEmailData(false);
    }
  }, []);

  const retrieveJobOrders = useCallback(async (JID, OID) => {
    try {
      setLoadingOrder(true);
      getJobOrders(JID).then((newOrders) => {
        const newOrderData =
          newOrders.find((item) => item.ORDER_ID === parseInt(OID)) || null;
        const newUnitMaterials = newOrderData
          ? JSON.parse(newOrderData.ORDER_ITEMS) || []
          : [];

        setLoadingOrder(false);
        setOrderData(newOrderData);
        setUnitMaterials(newUnitMaterials);
      });
    } catch (e) {
      console.error(e);
      setLoadingOrder(false);
    }
  }, []);

  useEffect(() => {
    if (supplierId > 0) retrieveEmailData(jobId, supplierId);
    else retrieveJobAddress(jobId, unitId);
    retrieveJobOrders(jobId, orderId);
  }, [jobId, orderId, supplierId, unitId]);

  return (
    <Container component="main">
      <Box sx={{ mb: 10, mt: 10 }}>
        <Card>
          <CardHeader
            title={
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <a
                    className={classes.navLink}
                    href={websiteURL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography variant="h5" color="white">
                      HORIZON CARPENTRY
                    </Typography>
                  </a>
                </Grid>
                <Grid item>
                  <Typography variant="h6" color="white">
                    PURCHASE ORDER
                  </Typography>
                </Grid>
              </Grid>
            }
            sx={{ backgroundColor: "orange" }}
          />
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Grid container justifyContent="space-between" spacing={4}>
                  <Grid item>
                    <Typography variant="body2">PO BOX 5874</Typography>
                    <Typography variant="body2">
                      Elgin, Illnois 60121
                    </Typography>
                    <Typography variant="body2">
                      <strong>Office:</strong> 815-572-5656
                    </Typography>
                    <Typography variant="body2">
                      <strong>Fax:</strong> 815-572-5011
                    </Typography>
                  </Grid>
                  <Grid item>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Order #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Start Date</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Delivery Date</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{orderId}</TableCell>
                            <TableCell>
                              {loadingEmailData ? (
                                <Skeleton variant="text" width="100px" />
                              ) : emailData?.RELEASE_DATE ? (
                                convertDate(emailData?.RELEASE_DATE)
                              ) : (
                                ""
                              )}
                            </TableCell>
                            <TableCell>
                              {loadingOrder ? (
                                <Skeleton variant="text" width="100px" />
                              ) : (
                                orderData?.PO_CREATE_DATE &&
                                convertDate(orderData.PO_CREATE_DATE)
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardHeader
                        title={<Typography variant="h6">To:</Typography>}
                      />
                      <Divider />
                      <CardContent>
                        {loadingEmailData ? (
                          <>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="50%" />
                            <Skeleton variant="text" width="50%" />
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              {emailData?.NAME}
                            </Typography>
                            <Typography variant="body2">
                              {emailData?.CONTACT}
                            </Typography>
                            <Typography variant="body2">
                              {emailData?.ADDRESS}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Phone: </strong> {emailData?.PHONE}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Fax: </strong> {emailData?.FAX}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardHeader
                        title={<Typography variant="h6">Ship To:</Typography>}
                      />
                      <Divider />
                      <CardContent>
                        {loadingEmailData ? (
                          <>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="50%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="50%" />
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>Project:</strong> {emailData?.DESCRIPTION}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Bld/Lot:</strong> {emailData?.JOB_DESC}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Job Id:</strong> {jobId}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Address/Unit:</strong>{" "}
                              {emailData?.DESCRIPTION1}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Builder:</strong> {emailData?.BUILDER}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    {loadingOrder ? (
                      <>
                        <Skeleton variant="text" width="50px" />
                        <Skeleton variant="text" width="50px" />
                      </>
                    ) : (
                      <>
                        <Typography variant="body2">
                          <strong>Phase: </strong> {orderData?.PHASE_ID}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Load: </strong> {orderData?.LOAD || 1}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {loadingEmailData ? (
                      <>
                        <Skeleton variant="text" width="80px" />
                        <Skeleton variant="text" width="80px" />
                        <Skeleton variant="text" width="50px" />
                      </>
                    ) : (
                      <>
                        <Typography variant="body2">
                          <strong>Model: </strong>
                          {emailData?.MODEL}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Elevation: </strong>
                          {emailData?.ELEVATION}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Type: </strong>
                          {emailData?.SHORT_DESC}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {user?.USER_ROLE !== "FOREMAN" && (
                <Grid item xs={12}>
                  <TotalsSummary
                    direction="horizontal"
                    data={{ unitMaterials, taxRate }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <UnitMaterialsTable
                  title="Unit materials"
                  hiddenColumns={
                    user?.USER_ROLE === "FOREMAN"
                      ? [
                          "PROJECT_DESCRIPTION",
                          "JOB_DESC",
                          "UNIT_ID",
                          "PHASE_ID",
                          "NEW_ITEM_QTY",
                          "ITEM_COST", // hide this column for foreman
                          "LINE_TOTAL", // hide this column for foreman
                        ]
                      : [
                          "PROJECT_DESCRIPTION",
                          "JOB_DESC",
                          "UNIT_ID",
                          "PHASE_ID",
                          "NEW_ITEM_QTY",
                        ]
                  }
                  items={unitMaterials}
                  loading={loadingOrder}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Special Notes/Shipping Instructions/Colors"
                  value={orderData?.SPECIFICATIONS || ""}
                  rows={3}
                  fullWidth
                  multiline
                  disabled
                  InputLabelProps={{
                    shrink: !!orderData?.SPECIFICATIONS,
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
